import type { JsonApiDocument, JsonApiResource, JsonApiResourceIdentifier } from './types';

/**
 * Chiave sotto cui viene conservato il `meta` **per-risorsa** di JSON:API.
 *
 * Il sigillo `$` non può collidere con un attributo del BE (sono identificatori camelCase
 * serializzati da proprietà C#), quindi non serve la disambiguazione che è stata necessaria
 * per `type` su `project-tasks`.
 *
 * Ci vive oggi `masked` (BE v1.102.0): `true` quando il server ha oscurato dei campi perché
 * il chiamante non è autorizzato a vederli. Senza conservarlo, una descrizione oscurata è
 * **indistinguibile da una vuota** — vedi `shared/lib/masked.ts`.
 */
export const RESOURCE_META = '$meta';

export interface Denormalized {
  type: string;
  id: string;
  /** `meta` per-risorsa del documento JSON:API (vedi RESOURCE_META). */
  [RESOURCE_META]?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Walk a JSON:API document and produce a denormalized JS object where
 * relationships are inlined from `included`. Cycle-safe via resolved-map.
 *
 * Contratto v1.7.0: attributi e relationships arrivano gia' in camelCase dal BE,
 * nessuna conversione di casing necessaria. Solo `type` e route restano kebab-case.
 */
export function denormalizeOne(
  resource: JsonApiResource,
  includedMap: Map<string, JsonApiResource>,
  resolved: Map<string, Denormalized> = new Map()
): Denormalized {
  const key = `${resource.type}:${resource.id}`;
  const cached = resolved.get(key);
  if (cached) return cached;

  /**
   * Alcune risorse espongono un attributo che si chiama `type` (project-tasks: il livello WBS;
   * erp-attendance-sources: il genere di sorgente). Collide con il `type` JSON:API di primo
   * livello — e siccome gli attributi si spargono **dopo**, senza questa rinomina l'attributo
   * vincerebbe e `row.type` non sarebbe più il tipo di risorsa. Il guasto è silenzioso: tutto
   * ciò che confronta `row.type` smette di corrispondere senza errori.
   */
  const rawAttrs = resource.attributes ?? {};
  const attrs: Record<string, unknown> = { ...rawAttrs };
  const ALIAS_TYPE: Record<string, string> = {
    'project-tasks': 'taskType',
    'erp-attendance-sources': 'sourceType',
    // BE v1.149 (modulo Assistenza): il genere di campo di un argomento — `text` | `textarea` |
    // `date` | `number` | `choice`. Terza risorsa con la stessa collisione.
    'support-topic-fields': 'fieldType',
  };
  const alias = ALIAS_TYPE[resource.type];
  if ('type' in attrs && alias) {
    attrs[alias] = attrs.type;
    delete attrs.type;
  }

  const out: Denormalized = {
    type: resource.type,
    id: resource.id,
    ...attrs,
  };
  // Il `meta` per-risorsa va conservato PRIMA degli attributi mancanti: è l'unico posto in
  // cui il server dice «questi campi te li ho oscurati». Vale sia sulla risorsa primaria sia
  // su quelle in `included`, che JADNC serializza allo stesso livello.
  if (resource.meta && typeof resource.meta === 'object') {
    out[RESOURCE_META] = resource.meta;
  }
  resolved.set(key, out);

  if (resource.relationships) {
    for (const [name, rel] of Object.entries(resource.relationships)) {
      if (!rel.data) {
        out[name] = null;
        continue;
      }
      if (Array.isArray(rel.data)) {
        out[name] = rel.data.map((id) => resolveIdentifier(id, includedMap, resolved));
      } else {
        out[name] = resolveIdentifier(rel.data, includedMap, resolved);
      }
    }
  }

  return out;
}

function resolveIdentifier(
  id: JsonApiResourceIdentifier,
  includedMap: Map<string, JsonApiResource>,
  resolved: Map<string, Denormalized>
): Denormalized | JsonApiResourceIdentifier {
  const full = includedMap.get(`${id.type}:${id.id}`);
  if (!full) return id;
  return denormalizeOne(full, includedMap, resolved);
}

export function denormalize<T extends Denormalized = Denormalized>(doc: JsonApiDocument): T | T[] | null {
  if (!doc.data) return null;
  const includedMap = new Map<string, JsonApiResource>();
  for (const inc of doc.included ?? []) {
    includedMap.set(`${inc.type}:${inc.id}`, inc);
  }
  const resolved = new Map<string, Denormalized>();
  if (Array.isArray(doc.data)) {
    return doc.data.map((d) => denormalizeOne(d, includedMap, resolved)) as T[];
  }
  return denormalizeOne(doc.data, includedMap, resolved) as T;
}
